/**
 * VoiceInput — a mic button that dictates a message in the sender's chosen language, so people on
 * mobile web can "type" in Telugu/Hindi/etc. without installing a language keyboard. Uses the browser's
 * built-in speech recognition (Web Speech API), set to the selected "I type in" language. Renders
 * nothing when the browser doesn't support it.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square } from 'lucide-react';

// Map our language names → BCP-47 speech-recognition locales.
const SPEECH_LANG: Record<string, string> = {
  English: 'en-US', Arabic: 'ar-SA', Hindi: 'hi-IN', Urdu: 'ur-PK', Malayalam: 'ml-IN',
  Tamil: 'ta-IN', Telugu: 'te-IN', Bengali: 'bn-IN', Tagalog: 'fil-PH', Nepali: 'ne-NP',
  French: 'fr-FR', Spanish: 'es-ES', Russian: 'ru-RU', Chinese: 'zh-CN',
};

const getSR = (): any => (typeof window !== 'undefined' ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition : null);
export const voiceSupported = (): boolean => !!getSR();

/** `onText` receives the live transcript (base text the user already typed + what they're saying). */
const VoiceInput: React.FC<{ lang?: string; baseText?: string; onText: (t: string) => void; disabled?: boolean }> = ({ lang, baseText, onText, disabled }) => {
  const [listening, setListening] = useState(false);
  const recRef = useRef<any>(null);
  const baseRef = useRef<string>('');

  useEffect(() => () => { try { recRef.current?.abort(); } catch { /* noop */ } }, []);

  if (!getSR()) return null;

  const stop = () => { try { recRef.current?.stop(); } catch { /* noop */ } setListening(false); };

  const start = () => {
    const SR = getSR();
    if (!SR) return;
    const rec = new SR();
    rec.lang = (lang && SPEECH_LANG[lang]) || 'en-US';
    rec.interimResults = true;
    rec.continuous = false;
    baseRef.current = (baseText || '').trim();
    rec.onresult = (e: any) => {
      let heard = '';
      for (let i = 0; i < e.results.length; i++) heard += e.results[i][0].transcript;
      const prefix = baseRef.current ? baseRef.current + ' ' : '';
      onText((prefix + heard).trim());
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch { setListening(false); }
  };

  return (
    <>
      <style>{`@keyframes viPulse{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.5)}70%{box-shadow:0 0 0 8px rgba(239,68,68,0)}}`}</style>
      <button
        type="button"
        onClick={() => (listening ? stop() : start())}
        disabled={disabled}
        title={listening ? 'Stop' : 'Speak your message'}
        aria-label={listening ? 'Stop dictation' : 'Speak your message'}
        style={{ ...btn, ...(listening ? btnOn : {}) }}
      >
        {listening ? <Square size={16} /> : <Mic size={18} />}
      </button>
    </>
  );
};

const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 42, height: 42, borderRadius: 12, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer', flexShrink: 0 };
const btnOn: React.CSSProperties = { background: '#ef4444', borderColor: '#ef4444', color: '#fff', animation: 'viPulse 1.2s ease-in-out infinite' };

export default VoiceInput;
